/**
 * Navigation Registry
 * 
 * Frontend registry for plugin-provided navigation items.
 * Part of Core Hardening Phase 2.
 * 
 * Core navigation items are always available as fallbacks.
 * The backend API can extend/override them via plugins.
 * 
 * Merge strategy (Wave A9.14 – Manifest-First):
 * - Deduplicates items by `pluginId:id` (the proper unique identifier).
 * - API items (manifest-derived) override both core defaults AND
 *   IntegrationPoints items when IDs or paths collide.
 * - IntegrationPoints items are fallbacks for paths not in the manifest.
 * - Categories are fully data-driven:
 *   • Core defines its own categories (Knowledge, System) in CORE_NAV_CATEGORIES.
 *   • Plugins declare categories via manifest.navigation[].category
 *     (canonical, Wave A9.14) or integrationPoints.navigation[].category (fallback).
 *   • No path-based inference is used.
 *   • Multiple plugins can contribute items to the same category by title.
 * - Plugin-registered navigation items are injected directly.
 * 
 * Priority order (highest wins):
 *   1. Backend API items (derived from plugin.manifest.json)
 *   2. Manifest nav items registered by PluginLoader (A9.14)
 *   3. IntegrationPoints nav items (legacy fallback)
 *   4. Core defaults (CORE_NAV_ITEMS / CORE_NAV_CATEGORIES)
 *
 * Positional ordering:
 *   The final navigation order is always:
 *     [Core standalone: Dashboard, Chat]
 *     [Alphabetically sorted: plugin items, plugin categories, core non-System categories]
 *     [System — always last]
 *   This ensures System is always anchored at the end while everything else is
 *   discoverable in alphabetical order.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  getNavigation,
  type NavigationItem,
  type NavigationCategory,
} from '../../api/platform';
import { getAllPluginCapabilities } from '../capability-enforcement';

// Re-export types for consumers
export type { NavigationItem, NavigationCategory };

/**
 * A unified navigation entry: either a standalone item or a category dropdown.
 * Used by TopNav for single-pass ordered rendering.
 *
 * Wave 22.8: Enables proper interleaving of plugin items and categories
 * in the correct positional order:
 *   [Core pre-plugin] → [Alphabetically sorted middle] → [System]
 */
export type NavigationEntry =
  | { type: 'item'; data: NavigationItem }
  | { type: 'category'; data: NavigationCategory };

// ============================================================
// CORE NAVIGATION DEFAULTS
// These are always available even when the backend API is down.
// Plugins can extend navigation via the platform API.
// ============================================================

const now = new Date().toISOString();

/**
 * Standalone top-level items (no category dropdown).
 * Wave A9.12: Dashboard (first) and Chat (second) are the only standalone entries.
 * Settings has been moved into the System category dropdown.
 */
const CORE_NAV_ITEMS: NavigationItem[] = [
  {
    id: 'core-dashboard',
    pluginId: 'core',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'home',
    order: 0,
    group: 'main',
    registeredAt: now,
  },
  {
    id: 'core-chat',
    pluginId: 'core',
    label: 'Chat',
    path: '/chat',
    icon: 'message-circle',
    order: 1,
    group: 'main',
    registeredAt: now,
  },
];

const CORE_NAV_CATEGORIES: NavigationCategory[] = [
  {
    id: 'core-system',
    pluginId: 'core',
    title: 'System',
    icon: 'settings',
    order: 100,
    items: [
      {
        id: 'core-settings',
        pluginId: 'core',
        label: 'Settings',
        path: '/settings',
        icon: 'settings',
        order: 0,
        registeredAt: now,
      },
      {
        id: 'core-plugins',
        pluginId: 'core',
        label: 'Plugins',
        path: '/settings/plugins',
        icon: 'puzzle',
        order: 1,
        registeredAt: now,
      },
      {
        id: 'core-system-health',
        pluginId: 'core',
        label: 'System health',
        path: '/system-health',
        icon: 'heart-pulse',
        order: 2,
        // Every plugin's logs and the server's internals.
        requiredPermissions: ['admin'],
        registeredAt: now,
      },
    ],
  },
  {
    id: 'core-automation',
    pluginId: 'core',
    title: 'Automation',
    icon: 'bot',
    order: 15,
    items: [
      {
        id: 'core-workflows',
        pluginId: 'core',
        label: 'Workflows',
        path: '/workflows',
        icon: 'workflow',
        order: 4,
        registeredAt: now,
      },
      {
        id: 'core-runners',
        pluginId: 'core',
        label: 'Runners',
        path: '/runners',
        icon: 'play-circle',
        order: 5,
        registeredAt: now,
      },
    ],
  },
  {
    id: 'core-operations',
    pluginId: 'core',
    title: 'Operations',
    icon: 'activity',
    order: 16,
    items: [
      {
        id: 'core-graph',
        pluginId: 'core',
        label: 'Knowledge graph',
        path: '/graph',
        icon: 'share-2',
        order: 0,
        registeredAt: now,
      },
      {
        id: 'core-search',
        pluginId: 'core',
        label: 'Search',
        path: '/search',
        icon: 'search',
        order: 1,
        // The search console can search, and its index health counts, every user's indexed content.
        requiredPermissions: ['admin'],
        registeredAt: now,
      },
      {
        id: 'core-approvals',
        pluginId: 'core',
        label: 'Approvals',
        path: '/approvals',
        icon: 'check-circle',
        order: 2,
        registeredAt: now,
      },
      // Review §6.3: the Artifacts nav entry is hidden until the artifact
      // storage API exists — the page is only an honest "coming soon"
      // placeholder, and a dead nav item shouldn't ship in prod. The /artifacts
      // route is kept in App.tsx so a direct link still renders the placeholder.
      // Re-add this entry (order: 3) once the artifact controller is live.
      {
        id: 'core-workflow-runs',
        pluginId: 'core',
        label: 'Workflow Runs',
        path: '/workflow-runs',
        icon: 'list',
        order: 4,
        registeredAt: now,
      },
      {
        id: 'core-execution-traces',
        pluginId: 'core',
        label: 'Execution Traces',
        path: '/execution-traces',
        icon: 'activity',
        order: 5,
        registeredAt: now,
      },
    ],
  },
];

// ============================================================
// STANDALONE & SUPPRESSED PATHS
// ============================================================

/** Paths that are standalone top-level nav items (never grouped).
 * Wave A9.12: Dashboard (first), Chat (second).
 * Settings has been moved under the System category.
 */
const STANDALONE_PATHS = new Set(['/dashboard', '/chat']);

/** Dead backend items that should be suppressed entirely. */
const SUPPRESSED_PATHS = new Set<string>(['/plugins']); // Wave 22.6: /plugins is a System→Plugins settings page, not a standalone topnav entry

// ============================================================
// Merge helpers
// ============================================================

/** Case-insensitive A–Z comparison for menu labels. */
function compareLabels(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

/**
 * Sort menu items alphabetically by label (children too).
 * Dropdown contents are always A–Z; `order` only breaks label ties.
 */
function sortItemsAlphabetically(items: NavigationItem[]): NavigationItem[] {
  return [...items]
    .map(item => (item.children?.length
      ? { ...item, children: sortItemsAlphabetically(item.children) }
      : item))
    .sort((a, b) => compareLabels(a.label, b.label) || (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Merge API navigation items with core defaults.
 * Deduplicates by **id** (the proper unique identifier) AND by **path**.
 * API items override core defaults when IDs or paths collide.
 */
function mergeNavItems(apiItems: NavigationItem[], coreItems: NavigationItem[]): NavigationItem[] {
  const mergedById = new Map<string, NavigationItem>();
  const mergedByPath = new Map<string, NavigationItem>();
  
  // Core first (can be overridden by API)
  for (const item of coreItems) {
    const key = `${item.pluginId}:${item.id}`;
    mergedById.set(key, item);
    mergedByPath.set(item.path, item);
  }
  
  // API items win on collision (by ID or by path)
  for (const item of apiItems) {
    if (SUPPRESSED_PATHS.has(item.path)) continue; // skip dead links
    
    const key = `${item.pluginId}:${item.id}`;
    const existingByPath = mergedByPath.get(item.path);
    
    // P2-4: Warn on route/nav path collisions so plugin developers are alerted
    if (existingByPath && existingByPath.pluginId !== item.pluginId) {
      console.warn(
        `[NavigationRegistry] Route conflict: path "${item.path}" registered by plugin "${item.pluginId}" ` +
        `overrides existing registration by "${existingByPath.pluginId}". ` +
        `The earlier registration will be replaced.`,
      );
    }
    
    // If there's an existing item with the same path, remove it from mergedById
    if (existingByPath) {
      const oldKey = `${existingByPath.pluginId}:${existingByPath.id}`;
      mergedById.delete(oldKey);
    }
    
    mergedById.set(key, item);
    mergedByPath.set(item.path, item);
  }
  
  return Array.from(mergedById.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function isNavigationEntryCapabilityHealthy(pluginId: string): boolean {
  if (!pluginId || pluginId === 'core') return true;
  const caps = getAllPluginCapabilities().get(pluginId);
  if (!caps) return false;
  return caps.granted.size > 0 || caps.autoGranted.size > 0;
}

function filterItemsByCapabilities(items: NavigationItem[]): NavigationItem[] {
  return items.filter((item) => isNavigationEntryCapabilityHealthy(item.pluginId));
}

/**
 * Merge API navigation categories with core defaults.
 * Deduplicates by **id** (the proper unique identifier).
 * 
 * Special handling: "Main" categories are dissolved — their items are extracted
 * and returned separately to be merged into the standalone items list.
 */
function mergeNavCategories(
  apiCats: NavigationCategory[],
  coreCats: NavigationCategory[]
): { categories: NavigationCategory[]; extractedItems: NavigationItem[] } {
  const merged = new Map<string, NavigationCategory>();
  const extractedItems: NavigationItem[] = [];

  // Core categories keyed by id
  for (const cat of coreCats) {
    const key = `${cat.pluginId}:${cat.id}`;
    merged.set(key, { ...cat, items: [...cat.items] });
  }

  // API/plugin categories — merge items into existing category if title matches,
  // otherwise add as a new category. We match by title (case-insensitive) so that
  // multiple plugins can contribute items to the same category (e.g., "Content").
  // 
  // "Main" categories are special: they are dissolved and their items extracted.
  for (const cat of apiCats) {
    // Dissolve "Main" categories
    if (cat.title.toLowerCase() === 'main') {
      extractedItems.push(...(cat.items ?? []));
      continue;
    }
    // First try exact key match
    const key = `${cat.pluginId}:${cat.id}`;
    const existing = merged.get(key);
    if (existing) {
      // Merge items: deduplicate by path, new items win on collision
      const itemsByPath = new Map<string, NavigationItem>();
      for (const item of existing.items) {
        itemsByPath.set(item.path, item);
      }
      for (const item of (cat.items ?? [])) {
        if (!SUPPRESSED_PATHS.has(item.path)) {
          itemsByPath.set(item.path, item);
        }
      }
      existing.items = Array.from(itemsByPath.values());
    } else {
      // Check if a category with the same title already exists (from another plugin)
      const existingByTitle = Array.from(merged.values()).find(
        c => c.title.toLowerCase() === cat.title.toLowerCase()
      );
      if (existingByTitle) {
        // Merge into existing title-matched category
        const itemsByPath = new Map<string, NavigationItem>();
        for (const item of existingByTitle.items) {
          itemsByPath.set(item.path, item);
        }
        for (const item of (cat.items ?? [])) {
          if (!SUPPRESSED_PATHS.has(item.path)) {
            itemsByPath.set(item.path, item);
          }
        }
        existingByTitle.items = Array.from(itemsByPath.values());
      } else {
        // New category — filter out suppressed items
        merged.set(key, {
          ...cat,
          items: (cat.items ?? []).filter(item => !SUPPRESSED_PATHS.has(item.path)),
        });
      }
    }
  }

  return {
    categories: Array.from(merged.values()).sort((a, b) => a.order - b.order),
    extractedItems,
  };
}

/**
 * Post-merge pass: route items that declare a `category` into the correct
 * category and ensure standalone items really should be standalone.
 *
 * This is fully data-driven — categories come from:
 *   1. Core CORE_NAV_CATEGORIES definitions
 *   2. Plugin integrationPoints.navigationCategories
 *   3. Plugin integrationPoints.navigation[].category field
 *
 * No path-based inference is used. If an item's `category` field names a
 * category that doesn't exist yet, the category is lazily created.
 *
 * Wave 22.8 positional ordering:
 *   items:   [core standalone (Dashboard, Chat)] → [plugin standalone items]
 *   categories: [plugin categories] → [core categories (Knowledge, System)]
 *   orderedEntries: unified interleaved list for single-pass rendering
 */
function reconcileItemsAndCategories(
  items: NavigationItem[],
  categories: NavigationCategory[],
): { items: NavigationItem[]; categories: NavigationCategory[]; orderedEntries: NavigationEntry[] } {
  const catByTitle = new Map<string, NavigationCategory>();
  for (const cat of categories) catByTitle.set(cat.title, cat);

  // Collect ALL paths already in a category (including children)
  const pathsInCategories = new Set<string>();
  for (const cat of categories) {
    for (const item of cat.items ?? []) {
      pathsInCategories.add(item.path);
      for (const child of item.children ?? []) {
        pathsInCategories.add(child.path);
      }
    }
  }

  const finalItems: NavigationItem[] = [];

  for (const item of items) {
    // Skip suppressed
    if (SUPPRESSED_PATHS.has(item.path)) continue;

    // If already in a category (as item or child), skip standalone
    if (pathsInCategories.has(item.path)) continue;

    // Standalone items (Dashboard, Chat) are never grouped
    if (STANDALONE_PATHS.has(item.path)) {
      finalItems.push(item);
      continue;
    }

    // Route by declared category (data-driven, from plugin integrationPoints)
    const declaredCategory = item.category;

    if (declaredCategory) {
      // Place into the declared category
      let cat = catByTitle.get(declaredCategory);
      if (!cat) {
        // Lazily create the category
        cat = {
          id: `auto:${declaredCategory.toLowerCase().replace(/\s+/g, '-')}`,
          pluginId: item.pluginId,
          title: declaredCategory,
          icon: item.icon,
          order: 50,
          items: [],
        };
        categories.push(cat);
        catByTitle.set(declaredCategory, cat);
      }
      // Avoid duplicate within category
      if (!cat.items.some(ci => ci.path === item.path)) {
        cat.items.push(item);
      }
      pathsInCategories.add(item.path);
    } else {
      // No category declared — standalone top-level item.
      // Plugin items without a category are placed in the plugin nav block
      // between Chat and Knowledge (positional ordering handles this).
      finalItems.push(item);
    }
  }

  // Remove any category items that ended up duplicated in standalone list,
  // then put each dropdown's contents in A–Z order
  const standaloneSet = new Set(finalItems.map(i => i.path));
  for (const cat of categories) {
    cat.items = sortItemsAlphabetically(
      (cat.items ?? []).filter(i => !standaloneSet.has(i.path) || STANDALONE_PATHS.has(i.path)),
    );
  }

  // ── Positional ordering ────────────────────────────────────
  // Core standalone (Dashboard, Chat) are always first.
  const coreStandalone = finalItems
    .filter(i => i.pluginId === 'core')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const nonCoreItems = finalItems.filter(i => i.pluginId !== 'core');

  const validCategories = categories.filter(c => (c.items?.length ?? 0) > 0);

  const sortedItems = [...coreStandalone, ...nonCoreItems];

  return {
    items: sortedItems,
    categories: validCategories,
    orderedEntries: buildOrderedEntries(sortedItems, validCategories),
  };
}

/**
 * Build a unified, positionally-ordered list of navigation entries.
 *
 * Ordering:
 *   1. Core standalone items (Dashboard, Chat) — sorted by order
 *   2. Middle block: all non-standalone items + all non-System categories — sorted alphabetically
 *   3. System category — always pinned last
 *
 * Items inside every category dropdown (and their children) are A–Z too.
 * No plugin names, categories, or paths are hard-coded.
 */
function buildOrderedEntries(
  items: NavigationItem[],
  categories: NavigationCategory[],
): NavigationEntry[] {
  // 1. Core standalone (Dashboard, Chat) — always first, sorted by order
  const coreStandalone = items
    .filter(i => i.pluginId === 'core')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const sortedCategories = categories.map(c => ({ ...c, items: sortItemsAlphabetically(c.items ?? []) }));

  // 3. System — always last (pinned by id)
  const systemCat = sortedCategories.find(c => c.id === 'core-system');

  // 2. Middle block: all non-core items + all non-System categories, sorted alphabetically
  const nonCoreItems = items.filter(i => i.pluginId !== 'core');
  const nonSystemCats = sortedCategories.filter(c => c.id !== 'core-system');

  const middleBlock: { label: string; entry: NavigationEntry }[] = [
    ...nonCoreItems.map(i => ({ label: i.label, entry: { type: 'item' as const, data: i } })),
    ...nonSystemCats.map(c => ({ label: c.title, entry: { type: 'category' as const, data: c } })),
  ];
  middleBlock.sort((a, b) => compareLabels(a.label, b.label));

  return [
    // 1. Dashboard, Chat
    ...coreStandalone.map(i => ({ type: 'item' as const, data: i })),
    // 2. Alphabetically sorted middle (plugin items, plugin categories, core non-System categories)
    ...middleBlock.map(p => p.entry),
    // 3. System — always last
    ...(systemCat ? [{ type: 'category' as const, data: systemCat }] : []),
  ];
}

/**
 * Navigation registry state
 */
interface NavigationRegistryState {
  // Data
  items: NavigationItem[];
  categories: NavigationCategory[];
  /** Unified positionally-ordered entries for single-pass TopNav rendering (Wave 22.8). */
  orderedEntries: NavigationEntry[];
  
  // Plugin-registered items (injected by PluginLoader)
  pluginItems: NavigationItem[];
  pluginCategories: NavigationCategory[];
  
  // State
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // Actions
  load: () => Promise<void>;
  refresh: () => Promise<void>;
  getItemsByGroup: (group: string) => NavigationItem[];
  getVisibleItems: (userPermissions: string[]) => NavigationItem[];
  getVisibleCategories: (userPermissions: string[]) => NavigationCategory[];
  /** Returns the unified ordered navigation list, filtered by permissions (Wave 22.8). */
  getOrderedNavigation: (userPermissions: string[]) => NavigationEntry[];

  /**
   * Register a standalone navigation item from a plugin.
   * Called by the PluginLoader after reading integration-points.
   */
  registerPluginNavItem: (item: NavigationItem) => void;

  /**
   * Register a navigation category from a plugin.
   * Called by the PluginLoader after reading integration-points.
   */
  registerPluginCategory: (category: NavigationCategory) => void;

  reset: () => void;
}

/**
 * Check if user has required permissions
 */
function hasPermissions(requiredPermissions: string[] | undefined, userPermissions: string[]): boolean {
  if (!requiredPermissions?.length) return true;
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  if (userPermissions.includes('admin')) return true;
  return requiredPermissions.every(p => userPermissions.includes(p));
}

/**
 * Filter items by permissions, including children.
 * Items whose children are all filtered out lose their children array
 * but may still be shown themselves.
 *
 * When `userPermissions` is empty/undefined, all items are shown —
 * this avoids hiding plugin navigation while the permissions API
 * is still loading.  Once permissions resolve, restricted items
 * are properly filtered.
 */
function filterItemsByPermissions(items: NavigationItem[], userPermissions: string[]): NavigationItem[] {
  if (!items || !Array.isArray(items)) return [];

  // Permissions not yet loaded → show everything (don't block on async load)
  if (!userPermissions?.length) {
    return items.map(item => ({
      ...item,
      children: item.children ? [...item.children] : undefined,
    }));
  }
  
  return items
    .filter(item => hasPermissions(item.requiredPermissions, userPermissions))
    .map(item => ({
      ...item,
      children: item.children 
        ? filterItemsByPermissions(item.children, userPermissions)
        : undefined,
    }));
}

/**
 * Navigation registry store
 */
export const useNavigationRegistry = create<NavigationRegistryState>()(
  subscribeWithSelector((set, get) => ({
    items: CORE_NAV_ITEMS,
    categories: CORE_NAV_CATEGORIES,
    orderedEntries: buildOrderedEntries(CORE_NAV_ITEMS, CORE_NAV_CATEGORIES),
    pluginItems: [],
    pluginCategories: [],
    isLoading: false,
    error: null,
    lastUpdated: null,

    load: async () => {
      // Don't reload if already loaded recently (within 5 seconds)
      const { lastUpdated, isLoading } = get();
      if (isLoading) return;
      if (lastUpdated && Date.now() - lastUpdated.getTime() < 5000) return;

      set({ isLoading: true, error: null });
      
      try {
        const data = await getNavigation();
        const { pluginItems, pluginCategories } = get();

        // Merge API data with core defaults (API can override core items)
        // Merge categories first to extract items from dissolved "Main" categories
        const { categories: rawCategories, extractedItems } = mergeNavCategories(
          [...(data.categories || []), ...pluginCategories],
          CORE_NAV_CATEGORIES,
        );
        // Wave A9.14: Merge order ensures manifest-derived API items win over
        // IntegrationPoints items. API items (from backend/manifest) come LAST
        // so they override IntegrationPoints items on path collision.
        const rawItems = mergeNavItems(
          [...pluginItems, ...extractedItems, ...(data.items || [])],
          CORE_NAV_ITEMS
        );
        const { items, categories, orderedEntries } = reconcileItemsAndCategories(rawItems, rawCategories);

        set({
          items,
          categories,
          orderedEntries,
          isLoading: false,
          lastUpdated: new Date(),
        });
      } catch (_error) {
        // On failure, keep core defaults instead of emptying navigation
        // Still apply plugin items so plugin nav is visible offline
        const { pluginItems, pluginCategories } = get();
        const { categories: rawCategories, extractedItems } = mergeNavCategories(
          pluginCategories,
          CORE_NAV_CATEGORIES
        );
        const rawItems = mergeNavItems([...pluginItems, ...extractedItems], CORE_NAV_ITEMS);
        const { items, categories, orderedEntries } = reconcileItemsAndCategories(rawItems, rawCategories);

        set({
          items,
          categories,
          orderedEntries,
          isLoading: false,
          error: _error instanceof Error ? _error.message : 'Failed to load navigation',
          lastUpdated: new Date(),
        });
      }
    },

    refresh: async () => {
      set({ isLoading: true, error: null });
      
      try {
        const data = await getNavigation();
        const { pluginItems, pluginCategories } = get();

        const { categories: rawCategories, extractedItems } = mergeNavCategories(
          [...(data.categories || []), ...pluginCategories],
          CORE_NAV_CATEGORIES,
        );
        // Wave A9.14: API items (manifest-derived) come last to win on collision
        const rawItems = mergeNavItems(
          [...pluginItems, ...extractedItems, ...(data.items || [])],
          CORE_NAV_ITEMS
        );
        const { items, categories, orderedEntries } = reconcileItemsAndCategories(rawItems, rawCategories);

        set({
          items,
          categories,
          orderedEntries,
          isLoading: false,
          lastUpdated: new Date(),
        });
      } catch (_error) {
        // On failure, keep core defaults + plugin items
        const { pluginItems, pluginCategories } = get();
        const { categories: rawCategories, extractedItems } = mergeNavCategories(
          pluginCategories,
          CORE_NAV_CATEGORIES
        );
        const rawItems = mergeNavItems([...pluginItems, ...extractedItems], CORE_NAV_ITEMS);
        const { items, categories, orderedEntries } = reconcileItemsAndCategories(rawItems, rawCategories);

        set({
          items,
          categories,
          orderedEntries,
          isLoading: false,
          error: _error instanceof Error ? _error.message : 'Failed to refresh navigation',
          lastUpdated: new Date(),
        });
      }
    },

    getItemsByGroup: (group: string) => {
      const items = get().items;
      if (!items || !Array.isArray(items)) return [];
      return items.filter(item => item.group === group);
    },

    getVisibleItems: (userPermissions: string[]) => {
      const items = get().items;
      const permissionFiltered = filterItemsByPermissions(items, userPermissions);
      return filterItemsByCapabilities(permissionFiltered);
    },

    getVisibleCategories: (userPermissions: string[]) => {
      const categories = get().categories;
      if (!categories || !Array.isArray(categories)) return [];
      
      return categories
        .map(category => ({
          ...category,
          items: filterItemsByCapabilities(filterItemsByPermissions(category.items, userPermissions)),
        }))
        .filter(category => category.items.length > 0);
    },

    getOrderedNavigation: (userPermissions: string[]) => {
      const { orderedEntries } = get();
      if (!orderedEntries || !Array.isArray(orderedEntries)) return [];

      // Permissions not yet loaded → show everything
      if (!userPermissions?.length) {
        return orderedEntries
          .map(entry => {
            if (entry.type === 'item') {
              return isNavigationEntryCapabilityHealthy(entry.data.pluginId) ? entry : null;
            }
            const filtered = filterItemsByCapabilities(entry.data.items ? [...entry.data.items] : []);
            if (filtered.length === 0) return null;
            return {
              ...entry,
              data: { ...entry.data, items: filtered },
            };
          })
          .filter((e): e is NavigationEntry => e !== null);
      }

      return orderedEntries
        .map(entry => {
          if (entry.type === 'item') {
            // Filter standalone item by permissions
            if (!hasPermissions(entry.data.requiredPermissions, userPermissions)) return null;
            if (!isNavigationEntryCapabilityHealthy(entry.data.pluginId)) return null;
            return entry;
          }
          // Filter category items by permissions
          const filtered = filterItemsByCapabilities(filterItemsByPermissions(entry.data.items, userPermissions));
          if (filtered.length === 0) return null;
          return { ...entry, data: { ...entry.data, items: filtered } };
        })
        .filter((e): e is NavigationEntry => e !== null);
    },

    registerPluginNavItem: (item: NavigationItem) => {
      const { pluginItems, items, categories } = get();

      // Avoid duplicates by path
      if (pluginItems.some(pi => pi.path === item.path)) return;

      const newPluginItems = [...pluginItems, item];
      const rawItems = mergeNavItems(newPluginItems, items);
      const rawCategories = [...categories];
      const reconciled = reconcileItemsAndCategories(rawItems, rawCategories);

      set({
        pluginItems: newPluginItems,
        items: reconciled.items,
        categories: reconciled.categories,
        orderedEntries: reconciled.orderedEntries,
      });
    },

    registerPluginCategory: (category: NavigationCategory) => {
      const { pluginCategories, items, categories } = get();

      // Avoid duplicates by title (multiple plugins can contribute to same category via merge)
      if (pluginCategories.some(pc => pc.id === category.id)) return;

      const newPluginCategories = [...pluginCategories, category];
      const { categories: rawCategories, extractedItems } = mergeNavCategories(
        newPluginCategories,
        categories
      );
      const mergedItems = mergeNavItems([...extractedItems], items);
      const reconciled = reconcileItemsAndCategories(mergedItems, rawCategories);

      set({
        pluginCategories: newPluginCategories,
        items: reconciled.items,
        categories: reconciled.categories,
        orderedEntries: reconciled.orderedEntries,
      });
    },

    reset: () => {
      set({
        items: CORE_NAV_ITEMS,
        categories: CORE_NAV_CATEGORIES,
        orderedEntries: buildOrderedEntries(CORE_NAV_ITEMS, CORE_NAV_CATEGORIES),
        pluginItems: [],
        pluginCategories: [],
        isLoading: false,
        error: null,
        lastUpdated: null,
      });
    },
  }))
);

/**
 * Hook to use navigation items with permission filtering
 */
export function useNavigationItems(userPermissions: string[] = []) {
  const { isLoading, error, lastUpdated, load, getVisibleItems } = useNavigationRegistry();
  
  // Load from API on first mount (will merge with core defaults)
  if (!isLoading && !lastUpdated) {
    load();
  }
  
  return {
    items: getVisibleItems(userPermissions),
    isLoading,
    error,
    refresh: load,
  };
}

/**
 * Hook to use navigation categories with permission filtering
 */
export function useNavigationCategories(userPermissions: string[] = []) {
  const { isLoading, error, lastUpdated, load, getVisibleCategories } = useNavigationRegistry();
  
  // Load from API on first mount (will merge with core defaults)
  if (!isLoading && !lastUpdated) {
    load();
  }
  
  return {
    categories: getVisibleCategories(userPermissions),
    isLoading,
    error,
    refresh: load,
  };
}

/**
 * Hook for unified, positionally-ordered navigation (Wave 22.8).
 *
 * Positional ordering:
 *   The final navigation order is always:
 *     [Core standalone: Dashboard, Chat]
 *     [Alphabetically sorted: plugin items, plugin categories, core non-System categories]
 *     [System — always last]
 */
export function useOrderedNavigation(userPermissions: string[] = []) {
  const { isLoading, error, lastUpdated, load, getOrderedNavigation } = useNavigationRegistry();

  // Load from API on first mount (will merge with core defaults)
  if (!isLoading && !lastUpdated) {
    load();
  }

  return {
    entries: getOrderedNavigation(userPermissions),
    isLoading,
    error,
    refresh: load,
  };
}
